import { getFieldNameWithPrefix } from '@monorepo/shared/componentsV2/fields/fieldUtils';
import { SelectableOption } from '@monorepo/shared/types/SelectableOption';
import {
  compose,
  hasAtLeastOneValue,
  hasNoBlankValues,
  hasNoCommasInValues,
  hasUniqueNonEmptyValues,
} from '@monorepo/shared/utils/validators';
import {
  FormTemplateFieldConditionalRule,
  SelectFormFieldOptions,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';
import { ModalFieldWrapper } from '../styled';
import { AlphabetizeOptions } from './AlphabetizeOptions';
import {
  BaseEditGenericFieldFormProps,
  EditGenericFieldForm,
} from './GenericFieldForm/EditGenericFieldForm';
import { ChooseDefaultOptionField } from './shared/ChooseDefaultOptionField';
import { StyledListBuilderField } from './styled';
import { useCanOptionsFulfillConditionalRules } from './useCanOptionsFulfillConditionalRules';

type EditSelectFieldFormProps = {
  isConditionTriggerField: boolean;
  conditionalRules?: FormTemplateFieldConditionalRule[];
} & BaseEditGenericFieldFormProps;

// this is rendered within FinalForm so the inputs are managed by it
export const EditSelectFieldForm = (props: EditSelectFieldFormProps) => {
  const {
    isConditionTriggerField,
    conditionalRules,
    ...genericFormFieldProps
  } = props;
  const { prefix } = genericFormFieldProps;

  const optionsFieldName = getFieldNameWithPrefix('options', prefix);
  const { input } = useField(optionsFieldName);

  const fieldOptions = input.value as SelectFormFieldOptions;
  const defaultValueOptions: SelectableOption<string>[] = (
    fieldOptions || []
  ).map((option) => ({ label: option, value: option }));

  const toolTipText = useMemo(() => {
    if (!isConditionTriggerField) return undefined;

    return 'Because another field’s visibility is connected to this field, you will not be able to edit some of the options below. To edit all options, first remove the condition on this field.';
  }, [isConditionTriggerField]);

  const hasAllConditionalValues =
    useCanOptionsFulfillConditionalRules(conditionalRules);

  return (
    <EditGenericFieldForm {...genericFormFieldProps}>
      <ModalFieldWrapper>
        <StyledListBuilderField
          // icon tooltip + required * look odd together, plus it's likely already filled out
          // if it's been set as a trigger field (and needs the trigger field tooltip)
          isRequired={!toolTipText}
          label="Options"
          name={optionsFieldName}
          reorderableProps={{
            isReorderable: true,
            uniqueDragListId: 'reorderableFormFieldSelectOptions',
            listAriaLabel: 'Reorderable Form Field Select Options',
          }}
          toolTipText={toolTipText}
          validate={compose(
            hasNoBlankValues,
            hasAtLeastOneValue,
            hasNoCommasInValues,
            hasUniqueNonEmptyValues,
            hasAllConditionalValues,
          )}
        />
      </ModalFieldWrapper>
      <AlphabetizeOptions prefix={prefix} />
      <ChooseDefaultOptionField
        defaultValueOptions={defaultValueOptions}
        prefix={prefix}
      />
    </EditGenericFieldForm>
  );
};
